// src/components/Design/LeftSidebar.tsx
import { ActionIcon, Box, Tooltip } from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setActiveLeftSideBarTabInRedux } from "../designSlice";
import { FaCheckDouble, FaInfo, FaQuestion, FaLayerGroup } from "react-icons/fa6";
import { FaCog, FaExchangeAlt, FaProjectDiagram, FaUndo } from "react-icons/fa";
import { DesignSettings } from "./DesignSettings";
import { SpaceValidationTablePanel } from "./SpaceValidationTablePanel";
import HelpPanel from "./HelpPanel";
import { DesignLeftSideBarTabs } from "@multiconsult-group/multiplacer-core";
import { selectAllowedLeftSidebarTabs, selectVisibleLeftSidebarTab } from "./leftSidebarSelectors";
import { DeviceHistoryPanel } from "../history/DeviceHistoryPanel";
import { McBimItemDetails } from "../../components/McBimItem/McBimItemDetails";
import { RoutingGraphsPanel } from "./RoutingGraphsPanel";
import { DrofusChangesPanel } from "../../DrofusChanges/DrofusChangesPanel";
import { ViewsPanel } from "./ViewsPanel";
import { ComponentsPanel } from "./ComponentsPanel";
import { MdViewModule } from "react-icons/md";

export const LeftSidebar: React.FC = () => {
  const spaceValidationRun = useAppSelector((state) => state.design.spaceValidationRun);
  const activeModule = useAppSelector((s) => s.design.activeModule); // "CEILING" | "ROOM_VALIDATION" | undefined
  const visibleActiveTab = useAppSelector(selectVisibleLeftSidebarTab);

  // Om ingen modul er aktiv, vis ingenting i venstre sidepanel
  if (!activeModule) {
    return null;
  }

  return (
    <Box
      c="black"
      bg="#e1e1e1b8"
      h="100%"
      w="100%"
      style={{
        display: "flex",
        flexDirection: "column",
        backdropFilter: "blur(8px)",
        minHeight: 0,
      }}
    >
      <Box
        h="100%"
        w="100%"
        style={{
          display: "flex",
          alignItems: "stretch",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box style={{ zIndex: 2 }}>
          <IconVerticalBar />
        </Box>

        {visibleActiveTab != null && (
          <Box
            p="md"
            bg={[DesignLeftSideBarTabs.VIEWS, DesignLeftSideBarTabs.COMPONENTS].includes(visibleActiveTab) ? "white" : undefined}
            w="100%"
            h="100%"
            style={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              maxHeight: "100%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {visibleActiveTab === DesignLeftSideBarTabs.VIEWS && <ViewsPanel />}
            {visibleActiveTab === DesignLeftSideBarTabs.COMPONENTS && <ComponentsPanel />}
            {visibleActiveTab === DesignLeftSideBarTabs.CEILING_HELP && <HelpPanel />}

            {visibleActiveTab === DesignLeftSideBarTabs.CEILING_VALIDATIONS &&
              spaceValidationRun.active === true && <SpaceValidationTablePanel />}

            {visibleActiveTab === DesignLeftSideBarTabs.CEILING_SETTINGS && <DesignSettings />}
            {visibleActiveTab === DesignLeftSideBarTabs.DEVICE_HISTORY && <DeviceHistoryPanel />}
            {visibleActiveTab === DesignLeftSideBarTabs.MCBIMITEM_INFO && <McBimItemDetails />}
            {visibleActiveTab === DesignLeftSideBarTabs.ROUTING_GRAPHS && <RoutingGraphsPanel />}
            {visibleActiveTab === DesignLeftSideBarTabs.DROFUS_CHANGES && <DrofusChangesPanel />}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const IconVerticalBar: React.FC = () => {
  const activeTab = useAppSelector((state) => state.design.activeLeftSideBarTab);
  const spaceValidationRun = useAppSelector((state) => state.design.spaceValidationRun);
  const dispatch = useAppDispatch();

  // Hvilke tabs er i det hele tatt lov for denne modulen?
  const allowedTabs = useAppSelector(selectAllowedLeftSidebarTabs);

  // Konfig per tab (label, ikon, tooltip)
  const allIconConfigs: {
    value: DesignLeftSideBarTabs;
    label: string;
    icon: React.ComponentType<{ size?: number }>;
    tooltip: string;
    hide?: boolean;
  }[] = [
    {
      value: DesignLeftSideBarTabs.VIEWS,
      label: "Visninger",
      icon: FaLayerGroup,
      tooltip: "Visninger",
    },
    {
      value: DesignLeftSideBarTabs.COMPONENTS,
      label: "Komponenter",
      icon: MdViewModule,
      tooltip: "Komponenter",
    },
    {
      value: DesignLeftSideBarTabs.CEILING_VALIDATIONS,
      label: "Valideringer",
      icon: FaCheckDouble,
      hide: spaceValidationRun.active === false,
      tooltip: "Valideringer",
    },
    {
      value: DesignLeftSideBarTabs.CEILING_HELP,
      label: "Forklaringer",
      icon: FaQuestion,
      tooltip: "Forklaringer",
    },
    {
      value: DesignLeftSideBarTabs.CEILING_SETTINGS,
      label: "Innstillinger",
      icon: FaCog,
      tooltip: "Innstillinger",
    },
    {
      value: DesignLeftSideBarTabs.DEVICE_HISTORY,
      label: "Endringshistorikk",
      icon: FaUndo,
      tooltip: "Endringshistorikk",
    },
    {
      value: DesignLeftSideBarTabs.MCBIMITEM_INFO,
      label: "BIM-objektinfo",
      icon: FaInfo,
      tooltip: "BIM-objektinfo",
    },
    {
      value: DesignLeftSideBarTabs.ROUTING_GRAPHS,
      label: "Lagrede rør",
      icon: FaProjectDiagram,
      tooltip: "Lagrede rør",
    },
    {
      value: DesignLeftSideBarTabs.DROFUS_CHANGES,
      label: "dRofus-endringer",
      icon: FaExchangeAlt,
      tooltip: "dRofus-endringer",
    },
  ];

  const iconConfigs = allIconConfigs
    .filter((cfg) => allowedTabs.includes(cfg.value)) // filtrer på activeModule
    .filter((cfg) => !cfg.hide);

  const selectTab = (tab: DesignLeftSideBarTabs) => {
    if (activeTab === tab) {
      dispatch(setActiveLeftSideBarTabInRedux(undefined));
    } else {
      dispatch(setActiveLeftSideBarTabInRedux(tab));
    }
  };

  if (iconConfigs.length === 0) {
    return null;
  }

  return (
    <Box h="100%" bg="gray.2">
      <ActionIcon.Group orientation="vertical">
        {iconConfigs.map((iconItem) => {
          const Icon = iconItem.icon;
          const isActive = activeTab === iconItem.value;
          const isViews = iconItem.value === DesignLeftSideBarTabs.VIEWS;
          const isPanelTab = isViews || iconItem.value === DesignLeftSideBarTabs.COMPONENTS;

          return (
            <Tooltip label={iconItem.tooltip} key={iconItem.value} position="right">
              <ActionIcon
                style={isPanelTab ? {
                  height: isViews ? 104 : 124,
                  flexShrink: 0,
                  backgroundColor: isViews
                    ? (isActive ? "var(--mantine-color-orange-8)" : "var(--mantine-color-orange-0)")
                    : (isActive ? "var(--mantine-primary-color-filled)" : "var(--mantine-primary-color-light)"),
                  color: isActive ? "white" : isViews ? "var(--mantine-color-orange-9)" : "var(--mantine-primary-color-filled)",
                  borderColor: isViews ? "var(--mantine-color-orange-3)" : "var(--mantine-color-default-border)",
                } : undefined}
                variant={isActive ? "filled" : "default"}
                size="md"
                aria-label={iconItem.label}
                aria-expanded={isActive}
                color={isViews ? "orange" : isActive ? undefined : "gray"}
                onClick={() => selectTab(iconItem.value)}
              >
                {isPanelTab ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, transform: "rotate(-90deg)", whiteSpace: "nowrap", fontSize: 12, fontWeight: 600 }}>
                    <Icon size={13} />
                    {iconItem.label}
                  </span>
                ) : <Icon />}
              </ActionIcon>
            </Tooltip>
          );
        })}
      </ActionIcon.Group>
    </Box>
  );
};
